import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { usePost } from "../../hooks/usePost";
import {
  Card,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  Icon,
  IconContainer,
  LegendCircle,
} from "../../components/visuals/styledComponents";
import { darkGreen, graphPaletteV2, lightGreenBackground } from "../../styles";
import ProgressBar from "react-bootstrap/ProgressBar";
import greenFlag from "../../assets/svg/flag_green.svg";
import {
  EngagementByManager,
  StewardshipFilterParam,
} from "../../models/stewardshipModels";
import { generatePaletteColors } from "../../utils/graphUtils";

const StyledProgressBar = styled(ProgressBar)<{ $compact?: boolean }>`
  display: flex;
  flex-grow: 1;
  height: ${({ $compact }): string => ($compact ? "16px" : "12px")};
  cursor: pointer;
`;

const LegendContainer = styled.span`
  display: flex;
  overflow-x: auto;
  flex-direction: row;
  align-items: center;
  white-space: nowrap;
`;

export interface EngagementsByManagerBarProps {
  filters: StewardshipFilterParam;
  compact?: boolean;
  /**
   * Optional chart → filter drill-down.
   * When set, segment / legend click selects that manager in shared filters.
   */
  onManagerSelect?: (manager: EngagementByManager) => void;
}

export const EngagementsByManagerBar: React.FC<EngagementsByManagerBarProps> = ({
  filters,
  onManagerSelect,
  compact = false,
}) => {
  const [hoveredSecurityIndex, setHoveredSecurityIndex] = useState<number>(-1);

  const {
    data,
    sendData: getEngagementsByManager,
    isLoading,
  } = usePost<EngagementByManager[], StewardshipFilterParam>();

  useEffect(() => {
    getEngagementsByManager("GET_ENGAGEMENTS_BY_MANAGER", filters);
  }, [filters]);

  // Ensure there are enough colors by modifying existing ones if necessary
  const colors = generatePaletteColors(data || [], graphPaletteV2);

  const handleSelect = (row: EngagementByManager): void => {
    if (onManagerSelect) {
      onManagerSelect(row);
    }
  };

  return (
    <Card
      h={compact ? "165px" : "120px"}
      m={compact ? "0" : "0 20px"}
      isLoading={isLoading}
    >
      <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
        Engagements by manager
      </CompassParagraph>
      {onManagerSelect && (
        <CompassParagraph color="#8B909A" fontSize={11} mt={-4}>
          Click a segment or manager to filter the programme
        </CompassParagraph>
      )}
      <RowContainer jc="space-between" ai="center" mt={compact ? 24 : -12}>
        <StyledProgressBar $compact={compact}>
          {(data || []).map((row, index) => (
            <ProgressBar
              variant="custom"
              now={row.engagement_count}
              key={row.manager_id}
              style={{
                backgroundColor: colors[index],
                cursor: onManagerSelect ? "pointer" : "default",
              }}
              onMouseEnter={(): void => setHoveredSecurityIndex(index)}
              onMouseLeave={(): void => setHoveredSecurityIndex(-1)}
              onClick={(): void => handleSelect(row)}
            />
          ))}
        </StyledProgressBar>
        {!compact && (
          <IconContainer size={50} bg={lightGreenBackground} br={25} p={12}>
            <Icon src={greenFlag} />
          </IconContainer>
        )}
      </RowContainer>
      <RowContainer mt={compact ? 16 : -20} jc="flex-start" ai="center">
        <LegendContainer>
          {(data || []).map((row, index) => (
            <span
              key={row.manager_id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: onManagerSelect ? "pointer" : "default",
                marginRight: 8,
              }}
              onClick={(): void => handleSelect(row)}
              onMouseEnter={(): void => setHoveredSecurityIndex(index)}
              onMouseLeave={(): void => setHoveredSecurityIndex(-1)}
            >
              <LegendCircle color={colors[index]}></LegendCircle>
              <CompassParagraph
                color={hoveredSecurityIndex === index ? darkGreen : "#8B909A"}
                fontSize={12}
                ws="nowrap"
                cursor={onManagerSelect ? "pointer" : "default"}
              >{`${row.manager_name} (${row.engagement_count} engagements)`}</CompassParagraph>
            </span>
          ))}
        </LegendContainer>
      </RowContainer>
    </Card>
  );
};
